import { useStatsigClient } from "@statsig/react-bindings";
import logger from "@utils/logger";
import { GameModel, WalletModel, WalletTxMethod, WithdrawRequest, WithdrawResponse } from "kz-ui-sdk";
import { useCallback, useEffect, useRef } from "react";
import { EVENTS, EventName } from "./constants";
import {
  DepositSource,
  DepositStep,
  DuplicateField,
  EventMetadata,
  OTPFailedAttemptMetadata,
  OTPMethod,
  RegistrationMethod,
  RegistrationSource,
  RegistrationStep,
} from "./types";
import { DepositFunnelTracker, RegistrationFunnelTracker, getDepositAmountRange, isValidEvent } from "./utils";

/**
 * Unified Statsig tracking hook - replaces all specialized hooks
 * Simple, maintainable, and scalable approach
 */
export function useStatsigTracking() {
  const { client } = useStatsigClient();
  const pendingEvents = useRef<Array<{ eventName: string; value?: number; metadata?: Record<string, any> }>>([]);

  // Main tracking function - simple and flexible
  const track = useCallback(
    (eventName: EventName | string, metadata?: EventMetadata, value?: number) => {
      if (!client) {
        logger._console.warn("Statsig client not available, queueing event");
        // Store event for later if client not ready
        pendingEvents.current.push({ eventName, value, metadata });
        return;
      }

      // Validate event name (dev warning only)
      if (!isValidEvent(eventName) && !eventName.startsWith("act:") && !eventName.startsWith("evt:")) {
        logger._console.warn(`Event name "${eventName}" doesn't follow naming convention (act: or evt:)`);
      }

      try {
        // Clean, simple event logging
        client.logEvent(eventName, value, metadata || {});

        // Log successful event for debugging
        logger._console.log(`[Statsig] ${eventName}`, { value, metadata });
      } catch (error) {
        console.error("Failed to log Statsig event:", error);
        // Store failed event for retry
        pendingEvents.current.push({ eventName, value, metadata });
      }
    },
    [client],
  );

  // Process any pending events when client becomes available
  useEffect(() => {
    if (client && pendingEvents.current.length > 0) {
      const eventsToProcess = [...pendingEvents.current];
      pendingEvents.current = [];

      eventsToProcess.forEach(({ eventName, value, metadata }) => {
        track(eventName, metadata, value);
      });
    }
  }, [client, track]);

  // Flush pending events manually (useful for critical moments)
  const flushEvents = useCallback(() => {
    if (client && pendingEvents.current.length > 0) {
      const eventsToProcess = [...pendingEvents.current];
      pendingEvents.current = [];

      eventsToProcess.forEach(({ eventName, value, metadata }) => {
        track(eventName, metadata, value);
      });
    }
  }, [client, track]);

  // ===== SPECIALIZED TRACKING FUNCTIONS =====
  // These functions handle metadata construction internally, reducing component coupling

  // Registration Flow Tracking
  const trackRegisterClick = useCallback(
    (source: RegistrationSource, options?: { previousPage?: string }) => {
      const tracker = RegistrationFunnelTracker.getInstance();
      tracker.startRegistration();
      tracker.addStep(RegistrationStep.CLICK_REGISTER);

      track(EVENTS.CLICK_REGISTER, {
        source,
        previousPage: options?.previousPage || window.location.pathname,
        step: RegistrationStep.CLICK_REGISTER,
      });
    },
    [track],
  );

  const trackRegisterFormSubmit = useCallback(
    (
      method: RegistrationMethod,
      options?: {
        timeSpentOnForm?: number;
        retryCount?: number;
      },
    ) => {
      const tracker = RegistrationFunnelTracker.getInstance();
      tracker.addStep(RegistrationStep.FORM_SUBMIT);
      const retryCount = tracker.incrementAttempt("register_form_submit");

      track(EVENTS.REGISTER_FORM_SUBMIT, {
        method,
        step: RegistrationStep.FORM_SUBMIT,
        timeSpentOnForm: options?.timeSpentOnForm,
        retryCount: options?.retryCount || retryCount,
      });
    },
    [track],
  );

  const trackOTPSent = useCallback(
    (
      method: string,
      options?: {
        deliveryTime?: number;
        retryCount?: number;
      },
    ) => {
      const tracker = RegistrationFunnelTracker.getInstance();
      tracker.addStep(RegistrationStep.OTP_SENT);
      const retryCount = tracker.incrementAttempt("otp_sent");

      track(EVENTS.OTP_SENT, {
        method,
        step: RegistrationStep.OTP_SENT,
        deliveryTime: options?.deliveryTime,
        retryCount: options?.retryCount || retryCount,
      });
    },
    [track],
  );

  const trackOTPVerified = useCallback(
    (
      method: string,
      options?: {
        attemptsCount?: number;
        timeToVerify?: number;
      },
    ) => {
      const tracker = RegistrationFunnelTracker.getInstance();
      tracker.addStep(RegistrationStep.OTP_VERIFY);

      track(EVENTS.OTP_VERIFIED_SUCCESS, {
        method,
        step: RegistrationStep.OTP_VERIFY,
        attemptsCount: options?.attemptsCount || 1,
        timeToVerify: options?.timeToVerify,
      });
    },
    [track],
  );

  const trackOTPRequestFailed = useCallback(
    (error: any) => {
      const otpTracker = RegistrationFunnelTracker.getInstance();

      const metadata: OTPFailedAttemptMetadata = {
        method: OTPMethod.SMS,
        step: RegistrationStep.OTP_SENT,
        error,
        attemptsCount: otpTracker.incrementAttempt("otp_request"),
      };
      track(EVENTS.OTP_REQUEST_FAILED, metadata);
    },
    [track],
  );

  const trackOTPFailedAttempt = useCallback(
    (error: any) => {
      // Track OTP verification failure with analytics data
      const failTracker = RegistrationFunnelTracker.getInstance();
      const failMetadata = {
        method: OTPMethod.SMS,
        step: RegistrationStep.OTP_VERIFY,
        attemptsCount: failTracker.incrementAttempt("otp_verify_fail"),
        error,
      };

      track(EVENTS.OTP_FAILED_ATTEMPT, failMetadata);
    },
    [track],
  );

  const trackRegistrationComplete = useCallback(
    (
      method: RegistrationMethod,
      options?: {
        hasReferralCode?: boolean;
        referralCode?: string;
        phoneCountryCode?: string;
        campaignId?: string;
      },
    ) => {
      const tracker = RegistrationFunnelTracker.getInstance();
      tracker.addStep(RegistrationStep.REGISTRATION_COMPLETE);

      track(EVENTS.REGISTRATION_COMPLETE, {
        method,
        step: RegistrationStep.REGISTRATION_COMPLETE,
        hasReferralCode: options?.hasReferralCode || false,
        referralCode: options?.referralCode,
        totalTimeToComplete: Math.floor(tracker.getTotalTime() / 1000),
        stepsCompleted: tracker.getCompletedSteps(),
        phoneCountryCode: options?.phoneCountryCode,
        campaignId: options?.campaignId,
      });
    },
    [track],
  );

  const trackRegistrationDuplicateError = useCallback(
    (error: any, { duplicateFields }: { duplicateFields: DuplicateField[] }) => {
      const duplicateTracker = RegistrationFunnelTracker.getInstance();
      const duplicateMetadata = {
        method: RegistrationMethod.USERNAME_PASSWORD,
        duplicateFields,
        error,
        previousAttempts: duplicateTracker.incrementAttempt("duplicate_error"),
      };
      track(EVENTS.REGISTER_DUPLICATE_ERROR, duplicateMetadata);
    },
    [track],
  );

  // Deposit Flow Tracking
  const trackDepositClick = useCallback(
    (
      source: DepositSource,
      options?: {
        accountBalance?: number;
      },
    ) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.startDeposit();
      tracker.addStep(DepositStep.CLICK_DEPOSIT);

      track(EVENTS.CLICK_DEPOSIT, {
        source,
        previousPage: window.location.pathname,
        accountBalance: options?.accountBalance,
      });
    },
    [track],
  );

  const trackDepositMethodGroup = useCallback(
    (groupId: string, groupName: string, source: DepositSource) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.SELECT_GROUP);
      tracker.setSelectedData("groupId", groupId);
      tracker.setSelectedData("groupName", groupName);

      track(EVENTS.SELECT_DEPOSIT_METHOD_GROUP, {
        source,
        step: DepositStep.SELECT_GROUP,
        groupId,
        groupName,
      });
    },
    [track],
  );

  const trackDepositMethodIndividual = useCallback(
    (
      methodId: string,
      methodName: string,
      options?: {
        source?: DepositSource;
        methodKey?: string;
        groupId?: string;
        isRecommended?: boolean;
        fees?: number;
        processingTime?: string;
      },
    ) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.SELECT_METHOD);
      tracker.setSelectedData("methodId", methodId);
      tracker.setSelectedData("methodName", methodName);

      track(EVENTS.SELECT_DEPOSIT_METHOD_INDIVIDUAL, {
        source: options?.source,
        step: DepositStep.SELECT_METHOD,
        methodId,
        methodName,
        methodKey: options?.methodKey,
        groupId: options?.groupId,
        isRecommended: options?.isRecommended,
        fees: options?.fees,
        processingTime: options?.processingTime,
      });
    },
    [track],
  );

  const trackAutoFastTriggered = useCallback(
    (
      autoSelectedMethod: string,
      options?: {
        source?: DepositSource;
        originalMethod?: string;
        reason?: string;
        timeSaved?: string;
      },
    ) => {
      track(EVENTS.AUTO_FAST_TRIGGERED, {
        source: options?.source,
        step: DepositStep.SELECT_METHOD,
        originalMethod: options?.originalMethod,
        autoSelectedMethod,
        reason: options?.reason || "auto_selection",
        timeSaved: options?.timeSaved,
      });
    },
    [track],
  );

  const trackDepositAmountSelect = useCallback(
    (
      amount: number,
      options?: {
        source?: DepositSource;
        selectedMethod?: string;
        isQuickAmount?: boolean;
        bonusOffered?: number;
        currency?: string;
      },
    ) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.SELECT_AMOUNT);
      tracker.setSelectedData("amount", amount);

      track(EVENTS.SELECT_DEPOSIT_AMOUNT, {
        source: options?.source,
        step: DepositStep.SELECT_AMOUNT,
        amount,
        amountRange: getDepositAmountRange(amount),
        selectedMethod: options?.selectedMethod,
        bonusOffered: options?.bonusOffered,
        currency: options?.currency,
      });
    },
    [track],
  );

  const trackTopUpFailed = useCallback(
    (error: any, options?: { source?: DepositSource; currency?: string }) => {
      track(EVENTS.TOP_UP_FAILED, {
        source: options?.source,
        currency: options?.currency,
        error,
      });
    },
    [track],
  );

  const trackTopUpSubmit = useCallback(
    (
      amount: number,
      methodId: string,
      methodName: string,
      options?: {
        source?: DepositSource;
        currency?: string;
        totalFees?: number;
        estimatedProcessingTime?: string;
        bonusApplied?: number;
      },
    ) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.SUBMIT_TOP_UP);

      track(EVENTS.TOP_UP_SUBMIT, {
        source: options?.source,
        step: DepositStep.SUBMIT_TOP_UP,
        amount,
        amountRange: getDepositAmountRange(amount),
        methodId,
        methodName,
        currency: options?.currency,
        totalFees: options?.totalFees,
        estimatedProcessingTime: options?.estimatedProcessingTime,
        bonusApplied: options?.bonusApplied,
      });
    },
    [track],
  );

  const trackDepositSuccess = useCallback(
    (
      amount: number,
      methodId: string,
      transactionId: string,
      options?: {
        source?: DepositSource;
        methodName?: string;
        currency?: string;
        actualProcessingTime?: number;
        feesCharged?: number;
        bonusAwarded?: number;
      },
    ) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.DEPOSIT_COMPLETE);

      track(EVENTS.DEPOSIT_SUCCESS, {
        source: options?.source,
        step: DepositStep.DEPOSIT_COMPLETE,
        amount,
        amountRange: getDepositAmountRange(amount),
        methodId,
        methodName: options?.methodName,
        transactionId,
        currency: options?.currency,
        actualProcessingTime: options?.actualProcessingTime,
        feesCharged: options?.feesCharged,
        bonusAwarded: options?.bonusAwarded,
        totalDepositTime: Math.floor(tracker.getTotalTime() / 1000),
      });
    },
    [track],
  );

  const trackDepositFailed = useCallback(
    (
      amount: number,
      methodId: string,
      error: any,
      options?: {
        source?: DepositSource;
        methodName?: string;
        currency?: string;
        retryAttempt?: number;
        failurePoint?: string;
      },
    ) => {
      track(EVENTS.DEPOSIT_FAILED, {
        source: options?.source,
        step: DepositStep.SUBMIT_DEPOSIT, // Usually fails at submit
        amount,
        amountRange: getDepositAmountRange(amount),
        methodId,
        methodName: options?.methodName,
        currency: options?.currency,
        error,
        retryAttempt: options?.retryAttempt || 1,
        failurePoint: options?.failurePoint,
      });
    },
    [track],
  );

  const trackGameOpened = useCallback(
    (game: Partial<GameModel>) => {
      track(EVENTS.GAME_OPENED, {
        game,
      });
    },
    [track],
  );

  const trackWalletUpdated = useCallback(
    (wallet: Partial<WalletModel>) => {
      track(EVENTS.WALLET_UPDATED, {
        wallet,
      });
    },
    [track],
  );

  const trackGameError = useCallback(
    (error: any) => {
      track(EVENTS.GAME_ERROR, {
        error,
      });
    },
    [track],
  );

  const trackSessionTimeout = useCallback(() => {
    track(EVENTS.SESSION_TIMEOUT, {});
  }, [track]);

  const trackGameUnavailable = useCallback(
    (gameId: string) => {
      track(EVENTS.GAME_UNAVAILABLE, {
        gameId,
      });
    },
    [track],
  );

  const trackPromoClaimAttempt = useCallback(
    (promoType: WalletTxMethod) => {
      track(EVENTS.PROMO_CLAIM_ATTEMPT, {
        promoType,
      });
    },
    [track],
  );

  const trackPromoClaimSuccess = useCallback(
    (promoType: WalletTxMethod) => {
      track(EVENTS.PROMO_CLAIM_SUCCESS, {
        promoType,
      });
    },
    [track],
  );

  const trackPromoRewardApplied = useCallback(
    (promoId: string) => {
      track(EVENTS.PROMO_REWARD_APPLIED, {
        promoId,
      });
    },
    [track],
  );

  const trackPromoBonusExpired = useCallback(
    (promoId: string) => {
      track(EVENTS.PROMO_BONUS_EXPIRED, {
        promoId,
      });
    },
    [track],
  );

  const trackWithdrawalClick = useCallback(() => {
    track(EVENTS.CLICK_WITHDRAWAL, {});
  }, [track]);

  const trackWithdrawalSubmit = useCallback(
    (payload: WithdrawRequest) => {
      track(EVENTS.WITHDRAWAL_SUBMIT, {
        payload,
      });
    },
    [track],
  );

  const trackWithdrawalSuccess = useCallback(
    (response: WithdrawResponse) => {
      track(EVENTS.WITHDRAWAL_SUCCESS, {
        response,
      });
    },
    [track],
  );
  const trackWithdrawalFailed = useCallback(
    (error: any) => {
      track(EVENTS.WITHDRAWAL_FAILED, {
        error,
      });
    },
    [track],
  );

  return {
    // Core tracking
    track,
    flushEvents,
    isReady: !!client,

    // Registration tracking
    trackRegisterClick,
    trackRegisterFormSubmit,
    trackOTPSent,
    trackOTPVerified,
    trackRegistrationComplete,
    trackOTPRequestFailed,
    trackOTPFailedAttempt,
    trackRegistrationDuplicateError,

    // Deposit tracking
    trackDepositClick,
    trackDepositMethodGroup,
    trackDepositMethodIndividual,
    trackAutoFastTriggered,
    trackDepositAmountSelect,
    trackTopUpSubmit,
    trackDepositSuccess,
    trackDepositFailed,
    trackTopUpFailed,

    // Gameplay tracking
    trackGameOpened,
    trackWalletUpdated,
    trackGameError,
    trackSessionTimeout,

    // Promotions tracking
    trackPromoClaimAttempt,
    trackPromoClaimSuccess,
    trackPromoRewardApplied,
    trackPromoBonusExpired,

    // Withdrawals tracking
    trackWithdrawalClick,
    trackWithdrawalSubmit,
    trackWithdrawalSuccess,
    trackWithdrawalFailed,
  };
}

/**
 * Hook to check Statsig status
 */
export function useStatsigStatus() {
  const { client } = useStatsigClient();

  return {
    isReady: !!client,
    hasClientKey: !!import.meta.env.VITE_STATSIG_CLIENT_KEY,
  };
}
